/**
 * @fileoverview Premium Badge Component for Skillzy.pro
 *
 * Status badges with color variants, dot indicators, and size options.
 *
 * @example
 * <Badge variant="success">Active</Badge>
 * <Badge variant="warning" dot>Pending</Badge>
 * <Badge variant="info" size="lg" removable onRemove={handleRemove}>Tag</Badge>
 */

import React, { forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';

/** @type {Record<string, string>} */
const variantClasses = {
  default: clsx(
    'bg-slate-100 text-slate-700',
    'dark:bg-slate-800 dark:text-slate-300'
  ),
  primary: clsx(
    'bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-600/20',
    'dark:bg-indigo-500/10 dark:text-indigo-400 dark:ring-indigo-500/20'
  ),
  success: clsx(
    'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20',
    'dark:bg-emerald-500/10 dark:text-emerald-400 dark:ring-emerald-500/20'
  ),
  warning: clsx(
    'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20',
    'dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20'
  ),
  error: clsx(
    'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-600/20',
    'dark:bg-rose-500/10 dark:text-rose-400 dark:ring-rose-500/20'
  ),
  info: clsx(
    'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-600/20',
    'dark:bg-sky-500/10 dark:text-sky-400 dark:ring-sky-500/20'
  ),
  purple: clsx(
    'bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-600/20',
    'dark:bg-purple-500/10 dark:text-purple-400 dark:ring-purple-500/20'
  ),
  outline: clsx(
    'bg-transparent text-slate-600 ring-1 ring-inset ring-slate-300',
    'dark:text-slate-400 dark:ring-slate-700'
  ),
};

/** @type {Record<string, string>} */
const dotColorClasses = {
  default: 'bg-slate-400 dark:bg-slate-500',
  primary: 'bg-indigo-500 dark:bg-indigo-400',
  success: 'bg-emerald-500 dark:bg-emerald-400',
  warning: 'bg-amber-500 dark:bg-amber-400',
  error: 'bg-rose-500 dark:bg-rose-400',
  info: 'bg-sky-500 dark:bg-sky-400',
  purple: 'bg-purple-500 dark:bg-purple-400',
  outline: 'bg-slate-400 dark:bg-slate-500',
};

/** @type {Record<string, string>} */
const sizeClasses = {
  xs: 'text-[10px] px-1.5 py-0.5 rounded',
  sm: 'text-xs px-2 py-0.5 rounded-md',
  md: 'text-xs px-2.5 py-1 rounded-md',
  lg: 'text-sm px-3 py-1 rounded-lg',
};

/** @type {Record<string, string>} */
const dotSizes = {
  xs: 'h-1 w-1',
  sm: 'h-1.5 w-1.5',
  md: 'h-1.5 w-1.5',
  lg: 'h-2 w-2',
};

/**
 * Premium Badge component for status indicators, tags, and labels.
 *
 * @param {Object} props
 * @param {'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' | 'purple' | 'outline'} [props.variant='default']
 * @param {'xs' | 'sm' | 'md' | 'lg'} [props.size='md']
 * @param {boolean} [props.dot=false] - Show status dot indicator
 * @param {boolean} [props.pulse=false] - Animate the dot with a pulse
 * @param {boolean} [props.removable=false] - Show remove (×) button
 * @param {Function} [props.onRemove] - Callback when remove button is clicked
 * @param {React.ReactNode} [props.icon] - Icon element
 * @param {string} [props.className]
 * @param {React.ReactNode} props.children
 */
const Badge = forwardRef(({
  variant = 'default',
  size = 'md',
  dot = false,
  pulse = false,
  removable = false,
  onRemove,
  icon,
  className,
  children,
  ...rest
}, ref) => {
  return (
    <AnimatePresence>
      <motion.span
        ref={ref}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.15 }}
        className={clsx(
          'inline-flex items-center gap-1.5',
          'font-medium leading-none',
          'whitespace-nowrap',
          sizeClasses[size] || sizeClasses.md,
          variantClasses[variant] || variantClasses.default,
          className
        )}
        {...rest}
      >
        {/* Status dot */}
        {dot && (
          <span className="relative flex shrink-0">
            <span
              className={clsx(
                'rounded-full',
                dotSizes[size] || dotSizes.md,
                dotColorClasses[variant] || dotColorClasses.default
              )}
            />
            {pulse && (
              <span
                className={clsx(
                  'absolute inset-0 rounded-full animate-ping',
                  dotColorClasses[variant] || dotColorClasses.default,
                  'opacity-40'
                )}
              />
            )}
          </span>
        )}

        {/* Icon */}
        {icon && (
          <span className="shrink-0 [&>svg]:h-3 [&>svg]:w-3">{icon}</span>
        )}

        {/* Content */}
        {children}

        {/* Remove button */}
        {removable && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.();
            }}
            className={clsx(
              'ml-0.5 -mr-0.5 shrink-0',
              'rounded-sm p-0.5',
              'opacity-60 hover:opacity-100',
              'transition-opacity duration-150',
              'focus:outline-none focus:ring-1 focus:ring-current'
            )}
            aria-label="Remove"
          >
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </motion.span>
    </AnimatePresence>
  );
});

Badge.displayName = 'Badge';

export default Badge;
